import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  ArrowLeft, Building2, CheckCircle, BarChart3, FileText, 
  Users, Clock, TrendingDown, ArrowRight, Shield
} from "lucide-react";

const TenUResLogo = ({ size = "text-4xl" }) => (
  <span className={`tenures-logo ${size}`}>
    Ten<span className="tenures-ur font-extrabold">UR</span>es
  </span>
);

const BenefitCard = ({ icon: Icon, title, description, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
  >
    <Card className="bg-white border-gray-200 h-full">
      <CardContent className="p-6">
        <div className="w-12 h-12 rounded-xl bg-[#05D38A]/10 flex items-center justify-center mb-4">
          <Icon className="w-6 h-6 text-[#05D38A]" />
        </div>
        <h3 className="font-bold text-[#111827] mb-2">{title}</h3>
        <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
      </CardContent>
    </Card>
  </motion.div>
);

const StatCard = ({ number, label }) => (
  <div className="text-center">
    <p className="text-4xl font-bold text-[#05D38A] mb-1">{number}</p>
    <p className="text-gray-600 text-sm">{label}</p>
  </div>
);

export default function ForProviders() {
  const navigate = useNavigate();

  const benefits = [
    {
      icon: FileText,
      title: "Pre-Qualifying Applications",
      description: "We filter out ineligible succession and RTB inquiries before they hit your desk, saving your team hours of assessment time."
    },
    {
      icon: BarChart3,
      title: "Optimizing Stock Utilization",
      description: "Our 'Match' engine identifies under-occupation and overcrowding trends in real-time, helping you manage your housing stock more effectively."
    },
    {
      icon: CheckCircle,
      title: "Standardizing Data",
      description: "Our Officer Discussion Guides ensure residents arrive with the exact data points you need to process their file efficiently."
    },
    {
      icon: Clock,
      title: "Reducing Response Times",
      description: "Pre-educated residents ask better questions. Your team spends less time explaining and more time processing."
    },
    {
      icon: TrendingDown,
      title: "Lowering Void Costs",
      description: "Faster succession decisions and mutual exchanges mean fewer empty properties and less rental loss."
    },
    {
      icon: Users,
      title: "Improving Resident Satisfaction",
      description: "Empowered residents are happier residents. Reduce complaints by giving them the knowledge they need upfront."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Link to="/">
            <TenUResLogo size="text-2xl" />
          </Link>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              className="text-[#111827] hover:text-[#05D38A] font-medium"
              onClick={() => navigate('/contact')}
            >
              Contact Us
            </Button>
            <Button 
              className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold"
              onClick={() => navigate('/contact')}
            >
              Request Demo
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#111827] to-[#1f2937]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
              <Building2 className="w-4 h-4 text-[#05D38A]" />
              <span className="text-sm font-medium text-white">For Housing Providers</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Reduce Admin.<br />
              <span className="text-[#05D38A]">Improve Resident Outcomes.</span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              TenURes isn't just for residents—it's a triage engine for Local Authorities and Housing Associations.
            </p>
            
            <Button 
              size="lg"
              className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold text-lg px-8 py-6 glow-mint"
              onClick={() => navigate('/contact')}
            >
              Request a Demo for Your Authority
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 border-b border-gray-200">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid grid-cols-3 gap-8"
          >
            <StatCard number="40%" label="Reduction in ineligible inquiries" />
            <StatCard number="2hrs" label="Average time saved per case" />
            <StatCard number="98%" label="Resident satisfaction rate" />
          </motion.div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#111827] mb-4">
              We Reduce the Burden on Frontline Staff
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our platform pre-qualifies residents and standardizes their applications, so your officers can focus on decision-making, not data-gathering.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <BenefitCard key={benefit.title} {...benefit} delay={0.1 * index} />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h2 className="text-3xl font-bold text-[#111827] mb-8 text-center">
              How TenURes Works for Providers
            </h2>

            <div className="space-y-8">
              <div className="flex items-start gap-6">
                <div className="w-10 h-10 rounded-full bg-[#05D38A] flex items-center justify-center text-white font-bold shrink-0">
                  1
                </div>
                <div>
                  <h3 className="font-bold text-[#111827] mb-2">Residents Self-Triage</h3>
                  <p className="text-gray-600">
                    Your residents use TenURes to assess their eligibility for succession, RTB, mutual exchange, or downsizing incentives. They answer structured questions that mirror your assessment criteria.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-10 h-10 rounded-full bg-[#05D38A] flex items-center justify-center text-white font-bold shrink-0">
                  2
                </div>
                <div>
                  <h3 className="font-bold text-[#111827] mb-2">Standardized Documentation</h3>
                  <p className="text-gray-600">
                    Each resident receives an "Officer Discussion Guide"—a PDF that summarizes their situation using professional terminology. Your officers receive cases with consistent, complete information.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-6">
                <div className="w-10 h-10 rounded-full bg-[#05D38A] flex items-center justify-center text-white font-bold shrink-0">
                  3
                </div>
                <div>
                  <h3 className="font-bold text-[#111827] mb-2">Faster Processing</h3>
                  <p className="text-gray-600">
                    With pre-qualified applications and standardized data, your team can process cases faster, reduce backlogs, and improve resident satisfaction scores.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-[#111827] rounded-2xl p-8 sm:p-12 text-center"
          >
            <Shield className="w-16 h-16 text-[#05D38A] mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Transform Your Housing Operations?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Join forward-thinking Local Authorities and Housing Associations using TenURes to reduce admin and improve outcomes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#05D38A] hover:bg-[#04B577] text-[#111827] font-semibold"
                onClick={() => navigate('/contact')}
              >
                Request a Demo
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-gray-600 text-white hover:bg-white/10"
                onClick={() => navigate('/contact')}
              >
                Contact Sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/succession" className="hover:text-[#05D38A]">Succession Triage</Link></li>
                <li><Link to="/exchange" className="hover:text-[#05D38A]">Mutual Exchange</Link></li>
                <li><Link to="/downsizing" className="hover:text-[#05D38A]">Downsizing</Link></li>
                <li><Link to="/unlock" className="hover:text-[#05D38A]">Right to Buy</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Resources</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/library" className="hover:text-[#05D38A]">Knowledge Hub</Link></li>
                <li><Link to="/about" className="hover:text-[#05D38A]">About Us</Link></li>
                <li><Link to="/providers" className="hover:text-[#05D38A]">For Providers</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/privacy" className="hover:text-[#05D38A]">Privacy & Security</Link></li>
                <li><Link to="/terms" className="hover:text-[#05D38A]">Terms of Service</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-[#111827] mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li><Link to="/contact" className="hover:text-[#05D38A]">Get in Touch</Link></li>
                <li><span>hello@tenures.app</span></li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-8 border-t border-gray-200">
            <TenUResLogo size="text-xl" />
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} TenURes. Advisory tool for social housing residents.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
